import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Table,
  Card,
  Row,
  Button,
  Space,
  DatePicker,
  Select,
  Typography,
  message,
  Upload,
  Input,
  Spin,
  Flex,
  Tooltip,
  Alert,
} from "antd";
import {
  DownloadOutlined,
  ReloadOutlined,
  SearchOutlined,
  InboxOutlined,
  QuestionCircleOutlined,
  CheckCircleOutlined,
  DatabaseOutlined,
} from "@ant-design/icons";
import { CardTitle, ColumnComponent } from "../../components/ui";
import { useParams, useSearchParams } from "react-router-dom";
import DataContext from "../../context/DataContext";
import dayjs from "dayjs";
import type { ColumnsType } from "antd/es/table";
import CustomerDetailModal from "../../components/CustomerDetailModal";
import type { TablePaginationConfig } from "antd/es/table";

const { Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { Dragger } = Upload;

interface QbdVsQboRow {
  Customer: string;
  Year: number;
  "# Invoices QBD": number;
  "Total Invoice Amount QBD": number;
  "# Invoices QBO": number;
  "Total Invoice amount QBO": number;
  "# Payments QBD": number;
  "Total Payments Amount QBD": number;
  "# Payments QBO": number;
  "Total Payment amount QBO": number;
  "# Credits QBD": number;
  "Total Credits Amount QBD": number;
  "# Credits QBO": number;
  "Total Credits amount QBO": number;
}

interface QbdVsQboResponse {
  page: number;
  pageSize: number;
  total: number;
  rows: QbdVsQboRow[];
  success: boolean;
}

interface SqliteStatus {
  hasDatabase: boolean;
  database?: {
    filename: string;
    originalName: string;
    fileSize: number;
    uploadDate: string;
    lastAccessed: string;
    connectionStatus: string;
  };
  message?: string;
}

const QbdVsQboPage: React.FC = () => {
  const { idClient } = useParams<{ idClient: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const dataContext = useContext(DataContext);

  // Type-safe access to context properties
  const customers = (dataContext as any)?.customers || [];

  // State
  const [data, setData] = useState<QbdVsQboRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [sqliteStatus, setSqliteStatus] = useState<SqliteStatus | null>(null);
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 20,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
  });

  // Filters
  const [customer, setCustomer] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [year, setYear] = useState<number | undefined>(2025);
  const [quarter, setQuarter] = useState<number | undefined>(1);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(
    null,
  );
  const [sortBy, setSortBy] = useState("Year");
  const [sortDir, setSortDir] = useState("desc");

  // Get current year for year options
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from(
    { length: currentYear - 2000 + 1 },
    (_, i) => currentYear - i,
  );
  const quarterOptions = [1, 2, 3, 4];

  // Fetch data function (no longer requires uploadId)
  const fetchData = useCallback(
    async (params: any = {}) => {
      if (!idClient || !sqliteStatus?.hasDatabase) return;

      setLoading(true);
      try {
        const currentPage = params.current || pagination.current || 1;
        const currentPageSize = params.pageSize || pagination.pageSize || 20;
        const currentSortBy = params.sortBy || sortBy;
        const currentSortDir = params.sortDir || sortDir;

        const queryParams = new URLSearchParams({
          idClient,
          page: currentPage.toString(),
          pageSize: currentPageSize.toString(),
          sortBy: currentSortBy,
          sortDir: currentSortDir,
          ...(customer && { customer }),
          ...(customerId && { customerId }),
          ...(year && { year: year.toString() }),
          ...(quarter && { quarter: quarter.toString() }),
          ...(dateRange &&
            dateRange[0] && { dateFrom: dateRange[0].format("YYYY-MM-DD") }),
          ...(dateRange &&
            dateRange[1] && { dateTo: dateRange[1].format("YYYY-MM-DD") }),
        });

        const response = await fetch(
          `${process.env.REACT_APP_DEV_API}/qbd/vs-qbo-report?${queryParams}&qbo_origin=${process.env.REACT_APP_QBO_ORIGIN}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("info")}`,
            },
          },
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result: QbdVsQboResponse = await response.json();

        if (result.success) {
          setData(result.rows);
          setPagination((prev) => ({
            ...prev,
            current: result.page,
            pageSize: result.pageSize,
            total: result.total,
          }));
        } else {
          message.error("Failed to fetch QBD vs QBO comparison");
        }
      } catch (error) {
        console.error("Error fetching QBD vs QBO comparison:", error);
        message.error("Failed to fetch data. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [
      idClient,
      sqliteStatus?.hasDatabase,
      pagination,
      sortBy,
      sortDir,
      customer,
      customerId,
      year,
      dateRange,
    ],
  );

  // Check SQLite database status
  const checkSqliteStatus = useCallback(async () => {
    if (!idClient) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_DEV_API}/qbd/sqlite-status/${idClient}?qbo_origin=${process.env.REACT_APP_QBO_ORIGIN}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("info")}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setSqliteStatus(result);

      // Auto-fetch data if database exists
      if (result.hasDatabase && result.success) {
        fetchData();
      }
    } catch (error) {
      console.error("Error checking SQLite status:", error);
      setSqliteStatus({
        hasDatabase: false,
        message: "Error checking database status",
      });
    }
  }, [idClient, fetchData]);

  // Currency formatter
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value || 0);
  };

  const formatCurrencyVariance = (value: number) => {
    const formatted = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value || 0);
    if (value === 0 || value === undefined) {
      return <Text type="success">{formatted}</Text>;
    }
    if (value > 0 || value < 0) {
      return <Text type="danger">{formatted}</Text>;
    }
    return formatted;
  };

  // State for customer detail modal
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailCustomer, setDetailCustomer] = useState<string>("");

  // Table columns
  const columns: ColumnsType<QbdVsQboRow> = [
    {
      title: "Customer",
      dataIndex: "Customer",
      key: "Customer",
      sorter: true,
      fixed: "left",
      width: 200,
      render: (text: string) => (
        <Text
          strong
          style={{ cursor: "pointer", color: "#17549a" }}
          onClick={() => {
            if (text) {
              setDetailCustomer(text);
              setDetailModalOpen(true);
            }
          }}
        >
          {text || "Unknown Customer"}
        </Text>
      ),
    },
    {
      title: "Year",
      dataIndex: "Year",
      key: "Year",
      sorter: true,
      width: 100,
      align: "center",
    },
    {
      title: "Quarter",
      dataIndex: "quarter",
      key: "Quarter",
      sorter: true,
      width: 100,
      align: "center",
    },
    {
      title: "Invoices",
      children: [
        {
          title: "QBD Invoices",
          children: [
            {
              title: "Count",
              dataIndex: "# Invoices QBD",
              key: "InvoicesQBD",
              sorter: true,
              width: 100,
              align: "right",
            },
            {
              title: "Total",
              dataIndex: "Total Invoice Amount QBD",
              key: "TotalInvoiceAmountQBD",
              sorter: true,
              width: 120,
              align: "right",
              render: (value: number) => {
                return formatCurrency(value);
              },
            },
          ],
        },
        {
          title: "QBO Invoices",
          children: [
            {
              title: "Count",
              dataIndex: "# Invoices QBO",
              key: "InvoicesQBO",
              sorter: true,
              width: 100,
              align: "right",
            },
            {
              title: "Total",
              dataIndex: "Total Invoice amount QBO",
              key: "TotalInvoiceAmountQBO",
              sorter: true,
              width: 120,
              align: "right",
              render: (value: number) => formatCurrency(value),
            },
          ],
        },
        {
          title: "Variances",
          children: [
            {
              title: "Variance Invoices",
              dataIndex: "Variance Invoices",
              key: "VarianceInvoices",
              sorter: true,
              width: 100,
              align: "right",
              render: (value: number) => (
                <Text type={value === 0 ? "success" : "danger"}>{value}</Text>
              ),
            },
            {
              title: "Variance Invoice Amount",
              dataIndex: "Variance Invoice Amount",
              key: "VarianceInvoiceAmount",
              sorter: true,
              width: 120,
              align: "right",
              render: (value: number) => {
                return formatCurrencyVariance(value);
              },
            },
          ],
        },
      ],
    },
    {
      title: "Payments",
      children: [
        {
          title: "QBD Payments",
          children: [
            {
              title: "Count",
              dataIndex: "# Payments QBD",
              key: "PaymentsQBD",
              sorter: true,
              width: 100,
              align: "right",
            },
            {
              title: "Total",
              dataIndex: "Total Payments Amount QBD",
              key: "TotalPaymentsAmountQBD",
              sorter: true,
              width: 120,
              align: "right",
              render: (value: number) => formatCurrency(value),
            },
          ],
        },
        {
          title: "QBO Payments",
          children: [
            {
              title: "Count",
              dataIndex: "# Payments QBO",
              key: "PaymentsQBO",
              sorter: true,
              width: 100,
              align: "right",
            },
            {
              title: "Total",
              dataIndex: "Total Payment amount QBO",
              key: "TotalPaymentsAmountQBO",
              sorter: true,
              width: 120,
              align: "right",
              render: (value: number) => formatCurrency(value),
            },
          ],
        },
        {
          title: "Variances",
          children: [
            {
              title: "Variance Payments",
              dataIndex: "Variance Payments",
              key: "VariancePayments",
              sorter: true,
              width: 100,
              align: "right",
              render: (value: number) => (
                <Text type={value === 0 ? "success" : "danger"}>{value}</Text>
              ),
            },
            {
              title: "Variance Payment Amount",
              dataIndex: "Variance Payment Amount",
              key: "VariancePaymentAmount",
              sorter: true,
              width: 120,
              align: "right",
              render: (value: number) => {
                return formatCurrencyVariance(value);
              },
            },
          ],
        },
      ],
    },
    {
      title: "Credits",
      children: [
        {
          title: "QBD Credits",
          children: [
            {
              title: "Count",
              dataIndex: "# Credits QBD",
              key: "CreditsQBD",
              sorter: true,
              width: 100,
              align: "right",
            },
            {
              title: "Total",
              dataIndex: "Total Credits Amount QBD",
              key: "TotalCreditsAmountQBD",
              sorter: true,
              width: 120,
              align: "right",
              render: (value: number) => formatCurrency(value),
            },
          ],
        },
        {
          title: "QBO Credits",
          children: [
            {
              title: "Count",
              dataIndex: "# Credits QBO",
              key: "CreditsQBO",
              sorter: true,
              width: 100,
              align: "right",
            },
            {
              title: "Total",
              dataIndex: "Total Credits amount QBO",
              key: "TotalCreditsAmountQBO",
              sorter: true,
              width: 120,
              align: "right",
              render: (value: number) => formatCurrency(value),
            },
          ],
        },
        {
          title: "Variances",
          children: [
            {
              title: "Variance Credits",
              dataIndex: "Variance Credits",
              key: "VarianceCredits",
              sorter: true,
              width: 100,
              align: "right",
              render: (value: number) => (
                <Text type={value === 0 ? "success" : "danger"}>{value}</Text>
              ),
            },
            {
              title: "Variance Credit Amount",
              dataIndex: "Variance Credit Amount",
              key: "VarianceCreditsAmount",
              sorter: true,
              width: 120,
              align: "right",
              render: (value: number) => {
                return formatCurrencyVariance(value);
              },
            },
          ],
        },
      ],
    },
  ];

  // Initialize from URL params
  useEffect(() => {
    const customerParam = searchParams.get("customer");
    const customerIdParam = searchParams.get("customerId");
    const yearParam = searchParams.get("year");
    const dateFromParam = searchParams.get("dateFrom");
    const dateToParam = searchParams.get("dateTo");
    const sortByParam = searchParams.get("sortBy");
    const sortDirParam = searchParams.get("sortDir");

    if (customerParam) setCustomer(customerParam);
    if (customerIdParam) setCustomerId(customerIdParam);
    if (yearParam) setYear(parseInt(yearParam));

    if (dateFromParam && dateToParam) {
      setDateRange([dayjs(dateFromParam), dayjs(dateToParam)]);
    }

    if (sortByParam) setSortBy(sortByParam);
    if (sortDirParam) setSortDir(sortDirParam);
  }, [searchParams]);

  // Check SQLite status on component mount
  useEffect(() => {
    if (idClient) {
      checkSqliteStatus();
    }
  }, [idClient]);

  // Upload file function (updated for new API)
  const handleUpload = async (file: File) => {
    if (!idClient) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("idClient", idClient);

      const response = await fetch(
        `${process.env.REACT_APP_DEV_API}/qbd/upload-sqlite?qbo_origin=${process.env.REACT_APP_QBO_ORIGIN}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("info")}`,
          },
          body: formData,
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        message.success(
          `SQLite database uploaded successfully: ${result.filename}`,
        );

        // Refresh SQLite status and auto-load data
        await checkSqliteStatus();

        // Clear URL params since we no longer use uploadId
        setSearchParams({});
      } else {
        message.error("Failed to upload SQLite database");
      }
    } catch (error) {
      console.error("Error uploading SQLite file:", error);
      message.error("Failed to upload file. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Effect to fetch data when SQLite status changes
  useEffect(() => {
    if (sqliteStatus?.hasDatabase) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sqliteStatus?.hasDatabase]);

  // Handle table change (pagination, sorting)
  const handleTableChange = (
    newPagination: TablePaginationConfig,
    filters: any,
    sorter: any,
  ) => {
    const sortByField = sorter.field || "Year";
    const sortDirection = sorter.order === "ascend" ? "asc" : "desc";

    setSortBy(sortByField);
    setSortDir(sortDirection);

    fetchData({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      sortBy: sortByField,
      sortDir: sortDirection,
    });
  };

  // Handle search
  const handleSearch = () => {
    // Update URL params (removed uploadId)
    const newParams = new URLSearchParams();
    if (customer) newParams.set("customer", customer);
    if (customerId) newParams.set("customerId", customerId);
    if (year) newParams.set("year", year.toString());
    if (dateRange && dateRange[0])
      newParams.set("dateFrom", dateRange[0].format("YYYY-MM-DD"));
    if (dateRange && dateRange[1])
      newParams.set("dateTo", dateRange[1].format("YYYY-MM-DD"));
    newParams.set("sortBy", sortBy);
    newParams.set("sortDir", sortDir);
    setSearchParams(newParams);

    fetchData({ current: 1 });
  };

  // Handle reset
  // const handleReset = () => {
  //   setCustomer("");
  //   setCustomerId("");
  //   setYear(undefined);
  //   setDateRange(null);
  //   setSortBy("Year");
  //   setSortDir("desc");
  //   setSearchParams({});
  //   fetchData({ current: 1, sortBy: "Year", sortDir: "desc" });
  // };

  // Export to CSV
  // const exportToCSV = () => {
  //   if (data.length === 0) {
  //     message.warning("No data to export");
  //     return;
  //   }

  //   const headers = [
  //     "Customer",
  //     "quarter",
  //     "Year",
  //     "# Invoices QBD",
  //     "Total Invoice Amount QBD",
  //     "# Invoices QBO",
  //     "Total Invoice amount QBO",
  //     "Variance Invoices",
  //     "Variance Invoice Amount",
  //     "# Payments QBD",
  //     "Total Payments Amount QBD",
  //     "# Payments QBO",
  //     "Total Payment amount QBO",
  //     "Variance Payments",
  //     "Variance Payment Amount",
  //     "# Credits QBD",
  //     "Total Credits Amount QBD",
  //     "# Credits QBO",
  //     "Total Credits amount QBO",
  //     "Variance Credits",
  //     "Variance Credit Amount",
  //   ];

  //   const csvContent =
  //     headers.join(",") +
  //     "\n" +
  //     data
  //       .map((row) =>
  //         headers
  //           .map((header) => {
  //             const value = row[header as keyof QbdVsQboRow];
  //             return typeof value === "string" ? `"${value}"` : value;
  //           })
  //           .join(","),
  //       )
  //       .join("\n");

  //   const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  //   const link = document.createElement("a");
  //   const url = URL.createObjectURL(blob);
  //   link.setAttribute("href", url);
  //   link.setAttribute(
  //     "download",
  //     `qbd-vs-qbo-comparison-${new Date().toISOString().split("T")[0]}.csv`,
  //   );
  //   link.style.visibility = "hidden";
  //   document.body.appendChild(link);
  //   link.click();
  //   document.body.removeChild(link);
  // };
  const exportToCSV = async () => {
    // Show loading indicator if desired
    setLoading(true);
    try {
      // Build query params with filters and all=true
      const queryParams = new URLSearchParams();
      if (idClient) queryParams.set("idClient", idClient);
      queryParams.set("sortBy", sortBy);
      queryParams.set("sortDir", sortDir);
      if (customer) queryParams.set("customer", customer);
      if (customerId) queryParams.set("customerId", customerId);
      if (year) queryParams.set("year", year.toString());
      if (quarter) queryParams.set("quarter", quarter.toString());
      if (dateRange && dateRange[0])
        queryParams.set("dateFrom", dateRange[0].format("YYYY-MM-DD"));
      if (dateRange && dateRange[1])
        queryParams.set("dateTo", dateRange[1].format("YYYY-MM-DD"));
      queryParams.set("all", "true"); // Only for export

      const response = await fetch(
        `${process.env.REACT_APP_DEV_API}/qbd/vs-qbo-report?${queryParams}&qbo_origin=${process.env.REACT_APP_QBO_ORIGIN}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("info")}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: QbdVsQboResponse = await response.json();

      if (!result.success || !result.rows || result.rows.length === 0) {
        message.warning("No data to export");
        return;
      }

      // CSV generation (same as before, but use result.rows)
      const headers = [
        "Customer",
        "quarter",
        "Year",
        "# Invoices QBD",
        "Total Invoice Amount QBD",
        "# Invoices QBO",
        "Total Invoice amount QBO",
        "Variance Invoices",
        "Variance Invoice Amount",
        "# Payments QBD",
        "Total Payments Amount QBD",
        "# Payments QBO",
        "Total Payment amount QBO",
        "Variance Payments",
        "Variance Payment Amount",
        "# Credits QBD",
        "Total Credits Amount QBD",
        "# Credits QBO",
        "Total Credits amount QBO",
        "Variance Credits",
        "Variance Credit Amount",
      ];

      const csvContent =
        headers.join(",") +
        "\n" +
        result.rows
          .map((row) =>
            headers
              .map((header) => {
                const value = row[header as keyof QbdVsQboRow];
                return typeof value === "string" ? `"${value}"` : value;
              })
              .join(","),
          )
          .join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `qbd-vs-qbo-comparison-${new Date().toISOString().split("T")[0]}.csv`,
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      message.error("Failed to export data. Please try again.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // const uploadProps = {
  //   name: "file",
  //   multiple: false,
  //   accept: ".sqlite,.db",
  //   beforeUpload: (file: File) => {
  //     handleUpload(file);
  //     return false; // Prevent default upload
  //   },
  //   showUploadList: false,
  // };

  return (
    <div className="general-view">
      <Row gutter={[24, 0]}>
        <ColumnComponent>
          <CardTitle title="QBD vs QBO Comparison Report" />
        </ColumnComponent>

        {/* Upload Section */}
        <ColumnComponent>
          <Card
            title={
              <Space>
                <DatabaseOutlined />
                {sqliteStatus?.hasDatabase
                  ? "QBD info Synced"
                  : "Upload QBD SQLite File"}
              </Space>
            }
            size="small"
            // extra={
            //   <Space>
            //     <Tooltip title="Export to CSV">
            //       <Button
            //         icon={<DownloadOutlined />}
            //         onClick={exportToCSV}
            //         disabled={data.length === 0}
            //       >
            //         Export
            //       </Button>
            //     </Tooltip>
            //     <Tooltip title="Refresh Data">
            //       <Button
            //         icon={<ReloadOutlined />}
            //         onClick={() => fetchData()}
            //         loading={loading}
            //         disabled={!sqliteStatus?.hasDatabase}
            //       >
            //         Refresh
            //       </Button>
            //     </Tooltip>
            //     {/* <Tooltip title="Reset all filters">
            //       <Button onClick={handleReset}>Reset</Button>
            //     </Tooltip> */}
            //   </Space>
            // }
          >
            {/* SQLite Status Display */}
            {/* {sqliteStatus?.hasDatabase && sqliteStatus.database && (
              <Alert
                message={
                  <Space>
                    <CheckCircleOutlined style={{ color: "#52c41a" }} />
                    <Text strong>
                      Database Active: {sqliteStatus.database.originalName}
                    </Text>
                  </Space>
                }
                description={
                  <Space direction="vertical" size="small">
                    <Text type="secondary">
                      Size:{" "}
                      {(sqliteStatus.database.fileSize / 1024 / 1024).toFixed(
                        2,
                      )}{" "}
                      MB
                    </Text>
                    <Text type="secondary">
                      Last accessed:{" "}
                      {new Date(
                        sqliteStatus.database.lastAccessed,
                      ).toLocaleString()}
                    </Text>
                    <Text type="secondary">
                      Status: {sqliteStatus.database.connectionStatus}
                    </Text>
                  </Space>
                }
                type="success"
                style={{ marginBottom: 16 }}
              />
            )} */}

            {/* Upload Section */}
            {/* <div style={{ marginBottom: 16 }}>
              <Dragger {...uploadProps} style={{ padding: "20px 0" }}>
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  {sqliteStatus?.hasDatabase
                    ? "Click or drag to replace current SQLite file"
                    : "Click or drag SQLite file to upload"}
                </p>
                <p className="ant-upload-hint">
                  Support for .sqlite and .db files only
                  {sqliteStatus?.hasDatabase && (
                    <>
                      <br />
                      <Text type="warning">
                        Uploading will replace the current database
                      </Text>
                    </>
                  )}
                </p>
              </Dragger>
            </div> */}

            {/* No Database Warning */}
            {!sqliteStatus?.hasDatabase && sqliteStatus !== null && (
              <Alert
                message="No SQLite Database Found"
                description="Please upload a QBD SQLite file to generate the comparison report."
                type="warning"
                showIcon
              />
            )}
          </Card>
        </ColumnComponent>

        {/* Filters Card */}
        <ColumnComponent>
          <Card title="Filter Data" size="small">
            <Space wrap size="middle">
              {/* Year Selector and Refresh Button */}
              <div>
                <Text strong>Quarter:</Text>
                <br />
                <Select
                  placeholder="Select Quarter"
                  style={{ width: 120 }}
                  value={quarter || undefined}
                  onChange={setQuarter}
                  allowClear={false}
                >
                  {quarterOptions.map((q) => (
                    <Option key={q} value={q}>{`Q${q}`}</Option>
                  ))}
                </Select>
              </div>
              <div>
                <Text strong>Year:</Text>
                <br />
                <Select
                  placeholder="Select Year"
                  style={{ width: 120 }}
                  value={year || currentYear}
                  onChange={setYear}
                  allowClear={false}
                >
                  {yearOptions.map((yr) => (
                    <Option key={yr} value={yr}>
                      {yr}
                    </Option>
                  ))}
                </Select>
              </div>
              {/* <div>
                <br />
                <Button
                  type="primary"
                  icon={<ReloadOutlined />}
                  loading={loading}
                  disabled={!sqliteStatus?.hasDatabase}
                  onClick={async () => {
                    setLoading(true);
                    try {
                      const response = await fetch(
                        `${process.env.REACT_APP_DEV_API}/qbo/sync/report-year?qbo_origin=${process.env.REACT_APP_QBO_ORIGIN}`,
                        {
                          method: "POST",
                          headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${localStorage.getItem("info")}`,
                          },
                          body: JSON.stringify({
                            year,
                            quarter,
                            clientId: idClient,
                          }),
                        },
                      );
                      const result = await response.json();
                      if (result.success && result.pulled === false) {
                        message.success(
                          `Loaded existing data for ${year}. Report refreshed.`,
                        );
                      } else if (result.success && result.pulled === true) {
                        message.success(
                          `Pulled QBO data for ${year} and refreshed report.`,
                        );
                      } else {
                        message.success(
                          result.message || "Year refresh failed",
                        );
                      }
                      // Reload report for selected year
                      setYear(year);
                      fetchData({ year });
                    } catch (e) {
                      message.error("Error refreshing year: " + e);
                    } finally {
                      setLoading(false);
                    }
                  }}
                >
                  Refresh Year from QBO
                </Button>
              </div> */}
              <div>
                <Text strong>Customer Name:</Text>
                <br />
                <Input
                  placeholder="Customer name contains..."
                  style={{ width: 200 }}
                  value={customer}
                  onChange={(e) => setCustomer(e.target.value)}
                  allowClear
                />
              </div>

              {/* <div>
                <Text strong>Year:</Text>
                <br />
                <Select
                  placeholder="All Years"
                  style={{ width: 120 }}
                  value={year || undefined}
                  onChange={setYear}
                  allowClear
                >
                  {yearOptions.map((yr) => (
                    <Option key={yr} value={yr}>
                      {yr}
                    </Option>
                  ))}
                </Select>
              </div> */}

              <div>
                <Text strong>Date Range:</Text>
                <br />
                <RangePicker
                  value={dateRange}
                  onChange={(dates) =>
                    setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)
                  }
                  format="YYYY-MM-DD"
                />
              </div>

              <div>
                <br />
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={handleSearch}
                  loading={loading}
                  disabled={!sqliteStatus?.hasDatabase}
                >
                  Apply Filters
                </Button>
              </div>
              <div>
                <br />
                <Button
                  icon={<DownloadOutlined />}
                  onClick={exportToCSV}
                  disabled={loading || !sqliteStatus?.hasDatabase}
                >
                  Export
                </Button>
              </div>
            </Space>
          </Card>
        </ColumnComponent>

        {/* Data Table */}
        <ColumnComponent>
          {loading && data.length === 0 ? (
            <div style={{ minHeight: "400px" }}>
              <Flex
                gap="middle"
                align="center"
                vertical
                justify="center"
                style={{ height: "400px" }}
              >
                <Spin size="large" />
                <Text>Loading QBD vs QBO comparison...</Text>
              </Flex>
            </div>
          ) : (
            <Card>
              <Table
                columns={columns}
                dataSource={data}
                loading={loading}
                pagination={pagination}
                onChange={handleTableChange}
                rowKey={(record) => {
                  return `${record.Customer}-${record.Year}`;
                }}
                bordered
                size="small"
                scroll={{ x: "100%" }}
                className="qbd-vs-qbo-table"
                summary={() => {
                  if (data.length === 0) return null;

                  const totals = data.reduce(
                    (acc, row) => ({
                      invoicesQBD: acc.invoicesQBD + row["# Invoices QBD"],
                      invoiceAmountQBD:
                        acc.invoiceAmountQBD + row["Total Invoice Amount QBD"],
                      invoicesQBO: acc.invoicesQBO + row["# Invoices QBO"],
                      invoiceAmountQBO:
                        acc.invoiceAmountQBO + row["Total Invoice amount QBO"],
                      paymentsQBD: acc.paymentsQBD + row["# Payments QBD"],
                      paymentAmountQBD:
                        acc.paymentAmountQBD + row["Total Payments Amount QBD"],
                      paymentsQBO: acc.paymentsQBO + row["# Payments QBO"],
                      paymentAmountQBO:
                        acc.paymentAmountQBO + row["Total Payment amount QBO"],
                      creditsQBD: acc.creditsQBD + row["# Credits QBD"],
                      creditAmountQBD:
                        acc.creditAmountQBD + row["Total Credits Amount QBD"],
                      creditsQBO: acc.creditsQBO + row["# Credits QBO"],
                      creditAmountQBO:
                        acc.creditAmountQBO + row["Total Credits amount QBO"],
                    }),
                    {
                      invoicesQBD: 0,
                      invoiceAmountQBD: 0,
                      invoicesQBO: 0,
                      invoiceAmountQBO: 0,
                      paymentsQBD: 0,
                      paymentAmountQBD: 0,
                      paymentsQBO: 0,
                      paymentAmountQBO: 0,
                      creditsQBD: 0,
                      creditAmountQBD: 0,
                      creditsQBO: 0,
                      creditAmountQBO: 0,
                    },
                  );

                  return (
                    <Table.Summary fixed>
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0}>
                          <Text strong>Page Total</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1} />
                        <Table.Summary.Cell index={2} align="right">
                          <Text strong>
                            {totals.invoicesQBD.toLocaleString()}
                          </Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={3} align="right">
                          <Text strong>
                            {formatCurrency(totals.invoiceAmountQBD)}
                          </Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={4} align="right">
                          <Text strong>
                            {totals.invoicesQBO.toLocaleString()}
                          </Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={5} align="right">
                          <Text strong>
                            {formatCurrency(totals.invoiceAmountQBO)}
                          </Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={6} align="right">
                          <Text strong>
                            {totals.paymentsQBD.toLocaleString()}
                          </Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={7} align="right">
                          <Text strong>
                            {formatCurrency(totals.paymentAmountQBD)}
                          </Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={8} align="right">
                          <Text strong>
                            {totals.paymentsQBO.toLocaleString()}
                          </Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={9} align="right">
                          <Text strong>
                            {formatCurrency(totals.paymentAmountQBO)}
                          </Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={10} align="right">
                          <Text strong>
                            {totals.creditsQBD.toLocaleString()}
                          </Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={11} align="right">
                          <Text strong>
                            {formatCurrency(totals.creditAmountQBD)}
                          </Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={12} align="right">
                          <Text strong>
                            {totals.creditsQBO.toLocaleString()}
                          </Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={13} align="right">
                          <Text strong>
                            {formatCurrency(totals.creditAmountQBO)}
                          </Text>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </Table.Summary>
                  );
                }}
              />
            </Card>
          )}
        </ColumnComponent>

        {/* Help Button */}
        <ColumnComponent>
          <div style={{ textAlign: "right" }}>
            <Tooltip title="This report compares QuickBooks Desktop (QBD) data with QuickBooks Online (QBO) data by Customer and Year. Upload a QBD SQLite file to see side-by-side comparison of invoices, payments, and credits between your desktop and online systems.">
              <Button
                icon={<QuestionCircleOutlined style={{ color: "#17549a" }} />}
                type="default"
              >
                Help
              </Button>
            </Tooltip>
          </div>
        </ColumnComponent>
      </Row>

      {/* Customer Detail Modal */}
      <CustomerDetailModal
        open={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        idClient={idClient || ""}
        customerName={detailCustomer}
        year={year}
        quarter={quarter}
        dateFrom={
          dateRange && dateRange[0]
            ? dateRange[0].format("YYYY-MM-DD")
            : undefined
        }
        dateTo={
          dateRange && dateRange[1]
            ? dateRange[1].format("YYYY-MM-DD")
            : undefined
        }
      />
    </div>
  );
};

export default QbdVsQboPage;
export { QbdVsQboPage };
